import React from 'react';
import { AlertTriangle, RefreshCcw, Home } from 'lucide-react';

/**
 * AppErrorBoundary — last line of defense against the "white screen of
 * nothing" symptom users reported on production. When any descendant
 * component throws during render (bad data shape, undefined prop access,
 * etc.), React 18 unmounts the whole tree and shows nothing. This boundary
 * catches that and renders a friendly card with a reload + back-home
 * affordance, plus a copy of the error so the admin can paste it back to
 * us during debugging.
 *
 * Specifically motivated by the report: "I clicked save on Featured
 * Authors and the page went white." The bug was unreproducible on preview
 * (data is clean), but production data may contain malformed entries that
 * crash a child component. With this boundary in place, the user keeps a
 * usable UI and we get the stack trace.
 */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to console so users on production who have devtools open
    // can paste it back to support — and for our own debugging.
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary] Caught render error:', error, info);
    this.setState({ info });
  }

  handleReload = () => {
    // Clear the cache layer so a corrupted cached payload doesn't keep
    // crashing on re-render after reload.
    try {
      const PREFIX = 'scb:cache:';
      const keys = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const k = localStorage.key(i);
        if (k && k.startsWith(PREFIX)) keys.push(k);
      }
      keys.forEach((k) => localStorage.removeItem(k));
    } catch { /* ignore */ }
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error?.message || String(this.state.error);
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center bg-zinc-950 px-6"
        data-testid="app-error-boundary"
      >
        <div className="max-w-lg w-full bg-white/5 border border-red-500/30 rounded-2xl p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-red-500/15 text-red-300 mx-auto mb-4 flex items-center justify-center">
            <AlertTriangle className="h-7 w-7" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Something just broke on screen</h2>
          <p className="text-sm text-gray-300 leading-relaxed mb-5">
            The page hit an unexpected error. Reload usually fixes it — your
            data is safe. If this keeps happening, please copy the message
            below and send it to support.
          </p>
          <pre
            className="text-left text-xs text-red-200/80 bg-zinc-900/80 border border-white/10 rounded-md p-3 mb-5 max-h-32 overflow-auto whitespace-pre-wrap break-words"
            data-testid="app-error-boundary-message"
          >
            {msg}
          </pre>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex items-center gap-2 bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
              data-testid="app-error-boundary-reload"
            >
              <RefreshCcw className="h-4 w-4" /> Reload page
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-gray-200 border border-white/10 font-semibold px-4 py-2 rounded-lg transition-colors"
              data-testid="app-error-boundary-home"
            >
              <Home className="h-4 w-4" /> Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
